import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  CheckCircle2, Phone, AlertTriangle, CalendarClock, CalendarCheck, Plus,
  Pencil, XCircle, ListTodo, SlidersHorizontal, X, Check, RotateCcw,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { toast } from "sonner";
import { ErrorView, EmptyState } from "@/components/StateViews";
import { ActivitySkeleton } from "@/components/Skeletons";
import { QualityChip, StageChip, ActivityTypeChip, PriorityChip, ActivityStatusChip } from "@/components/Chips";
import { SavedFilters } from "@/components/SavedFilters";
import FilterChips from "@/components/FilterChips";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadDrawer from "@/components/LeadDrawer";
import ActivityModal from "@/components/ActivityModal";
import DateRangePicker from "@/components/DateRangePicker";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTime, initials } from "@/lib/format";

function ActivityCard({ a, onAction, onOpenLead, onEdit, onRepeat, completed, busyId }) {
  const busy = busyId === a.id;
  return (
    <div data-testid={`activity-${a.id}`} className="rounded-2xl border border-sky/70 bg-white p-4 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <ActivityTypeChip value={a.activity_type} />
            <PriorityChip value={a.priority} />
            {completed && <ActivityStatusChip value={a.status} />}
          </div>
          <p className="mt-1.5 font-sora font-semibold text-navy">{a.activity_title}</p>
          <button onClick={() => onOpenLead(a.lead_id)} className="text-xs text-loop hover:underline">
            {a.lead_name || "Lead"}{a.business_name ? ` · ${a.business_name}` : ""}
          </button>
        </div>
        <div className="flex flex-shrink-0 flex-col items-end gap-1">
          <StageChip value={a.lead_stage} />
          <QualityChip value={a.lead_quality} />
        </div>
      </div>
      {a.activity_note && <p className="mt-2 rounded-lg bg-mist px-3 py-2 text-sm text-ink/80 line-clamp-3">{a.activity_note}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink/50">
        <span className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{fmtDateTime(a.due_at)}</span>
        {a.assigned_to_name && <span className="flex items-center gap-1"><span className="flex h-4 w-4 items-center justify-center rounded bg-navy text-[8px] font-bold text-white">{initials(a.assigned_to_name)}</span>{a.assigned_to_name}</span>}
        {a.campaign_name && <span className="rounded-full bg-sky/40 px-2 py-0.5 text-[10px] text-navy">{a.campaign_name}</span>}
        {a.owner_mismatch && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">Owner differs</span>}
      </div>
      {completed ? (
        <p className="mt-2 text-xs text-emerald-600">Completed {fmtDateTime(a.completed_at)}{a.completed_by_name ? ` by ${a.completed_by_name}` : ""}</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {a.phone && <a href={`tel:${a.phone}`} className="rounded-lg border border-sky p-2 text-loop hover:bg-sky/30"><Phone className="h-4 w-4" /></a>}
          <button data-testid={`activity-complete-${a.id}`} disabled={busy} onClick={() => onAction(a.id, "complete")} className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-2 text-xs font-medium text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"><CheckCircle2 className="h-3.5 w-3.5" />Done</button>
          <button disabled={busy} onClick={() => onAction(a.id, "snooze", 1)} className="rounded-lg border border-sky px-2.5 py-2 text-xs font-medium text-ink/70 hover:bg-mist disabled:opacity-50">+1d</button>
          <button disabled={busy} onClick={() => onAction(a.id, "snooze", 3)} className="rounded-lg border border-sky px-2.5 py-2 text-xs font-medium text-ink/70 hover:bg-mist disabled:opacity-50">+3d</button>
          <button data-testid={`activity-edit-${a.id}`} onClick={() => onEdit(a)} className="rounded-lg border border-sky p-2 text-loop hover:bg-sky/30"><Pencil className="h-3.5 w-3.5" /></button>
          {onRepeat && <button data-testid={`activity-repeat-${a.id}`} title="Repeat activity" onClick={() => onRepeat(a)} className="rounded-lg border border-sky p-2 text-ink/70 hover:bg-mist"><RotateCcw className="h-3.5 w-3.5" /></button>}
          <button data-testid={`activity-cancel-${a.id}`} disabled={busy} onClick={() => onAction(a.id, "cancel")} className="rounded-lg border border-sky p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"><XCircle className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

const FILTER_KEYS = ["activity_type", "priority", "assigned_to_user_id", "date_from", "date_to", "overdue_only"];
const blankFilters = () => Object.fromEntries(FILTER_KEYS.map((k) => [k, ""]));
const KEY_LABELS = { activity_type: "Type", priority: "Priority", assigned_to_user_id: "Assignee", date_from: "From", date_to: "To" };

export default function Activities() {
  const { refreshKey, users, setBadge } = useOutletContext();
  const { options, user } = useAuth();
  const [params, setParams] = useSearchParams();

  const initFilters = () => {
    const f = blankFilters();
    FILTER_KEYS.forEach((k) => { const v = params.get(k); if (v) f[k] = v; });
    return f;
  };
  const [applied, setApplied] = useState(initFilters);
  const [draft, setDraft] = useState(applied);
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("active");
  const [modal, setModal] = useState(null);
  const [repeatModal, setRepeatModal] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cancelId, setCancelId] = useState(null);

  const isManager = user?.role !== "Sales Executive";

  useEffect(() => {
    const next = {};
    FILTER_KEYS.forEach((k) => { if (applied[k]) next[k] = applied[k]; });
    setParams(next, { replace: true });
  }, [applied, setParams]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const send = {};
      ["activity_type", "priority", "assigned_to_user_id", "date_from", "date_to"].forEach((k) => {
        if (applied[k]) send[k] = applied[k];
      });
      const { data } = await minDelay(api.get("/activities", { params: send }));
      setData(data.data);
      setBadge?.(data.data.badge_count);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [setBadge, applied]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doCancel = async () => {
    const id = cancelId;
    setBusyId(id);
    try {
      await api.post(`/activities/${id}/cancel`);
      toast.success("Activity cancelled");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  const action = async (id, type, days) => {
    if (type === "cancel") { setCancelId(id); return; }
    setBusyId(id);
    try {
      if (type === "complete") await api.post(`/activities/${id}/complete`);
      else await api.post(`/activities/${id}/snooze`, { days });
      toast.success(type === "complete" ? "Activity completed" : `Snoozed ${days} day(s)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusyId(null); }
  };

  const setDraftFilter = (k, v) => setDraft((f) => ({ ...f, [k]: v }));
  const openFilters = () => { setDraft(applied); setShowFilters(true); };
  const applyFilters = () => { setApplied(draft); setShowFilters(false); };
  const resetDraft = () => setDraft(blankFilters());
  const cancelFilters = () => { setDraft(applied); setShowFilters(false); };
  const applySaved = (saved) => { const f = { ...blankFilters(), ...saved }; setApplied(f); setDraft(f); };
  const removeChip = (key) => { const next = { ...applied, [key]: "" }; setApplied(next); setDraft(next); };
  const clearAllChips = () => { setApplied(blankFilters()); setDraft(blankFilters()); };

  const appliedCount = Object.values(applied).filter(Boolean).length;
  const draftCount = Object.values(draft).filter(Boolean).length;
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const overdueOnly = !!applied.overdue_only;

  const chipLabel = (key, val) => {
    if (key === "overdue_only") return "Overdue only";
    if (key === "assigned_to_user_id") return `Assignee: ${users?.find((u) => u.id === val)?.name || val}`;
    return `${KEY_LABELS[key]}: ${val}`;
  };
  const chips = FILTER_KEYS.filter((k) => applied[k]).map((k) => ({ key: k, label: chipLabel(k, applied[k]) }));

  const FilterPanel = (
    <div className="space-y-3 rounded-2xl border border-sky/70 bg-white p-4" data-testid="activities-filter-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="activities-filter-type" value={draft.activity_type} onChange={(e) => setDraftFilter("activity_type", e.target.value)} className="h-9 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
          <option value="">All types</option>
          {options?.activity_types?.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="activities-filter-priority" value={draft.priority} onChange={(e) => setDraftFilter("priority", e.target.value)} className="h-9 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
          <option value="">All priorities</option>
          {options?.activity_priorities?.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {isManager && (
          <select data-testid="activities-filter-assigned" value={draft.assigned_to_user_id} onChange={(e) => setDraftFilter("assigned_to_user_id", e.target.value)} className="h-9 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
            <option value="">All assignees</option>
            {users?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2 rounded-lg border border-sky px-3 py-1.5 text-sm text-ink/80">
          <input type="checkbox" data-testid="activities-filter-overdue" checked={!!draft.overdue_only} onChange={(e) => setDraftFilter("overdue_only", e.target.checked ? "1" : "")} className="h-4 w-4 rounded border-sky text-loop" />
          Overdue only
        </label>
        <div className="ml-auto">
          <DateRangePicker
            from={draft.date_from}
            to={draft.date_to}
            onChange={({ created_from, created_to }) => { setDraftFilter("date_from", created_from); setDraftFilter("date_to", created_to); }}
            testid="activities-date-range"
            future
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky/50 pt-3">
        <SavedFilters module="activities" filters={applied} onApply={applySaved} activeCount={appliedCount} />
        <div className="flex items-center gap-2">
          <button onClick={resetDraft} disabled={!draftCount} data-testid="activities-filter-reset" className="flex items-center gap-1.5 rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 hover:bg-mist disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" />Reset</button>
          <button onClick={cancelFilters} data-testid="activities-filter-cancel" className="rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 hover:bg-mist">Cancel</button>
          <button onClick={applyFilters} disabled={!dirty} data-testid="activities-filter-apply" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-50"><Check className="h-4 w-4" />Apply Filters</button>
        </div>
      </div>
    </div>
  );

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><ListTodo className="h-7 w-7 text-loop" />Activities</h1>
        <p className="text-sm text-ink/60">{data ? `${data.badge_count} task(s) need attention today.` : "Loading…"}</p>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => showFilters ? cancelFilters() : openFilters()} data-testid="activities-filter-toggle" className="relative flex h-10 items-center gap-2 rounded-xl border border-sky bg-white px-3 text-sm font-medium text-navy hover:bg-mist">
          <SlidersHorizontal className="h-4 w-4" /> Filters
          {appliedCount > 0 && <span className="rounded-full bg-loop px-1.5 text-[11px] font-bold text-white">{appliedCount}</span>}
        </button>
        <button onClick={() => setModal("new")} data-testid="add-activity-button" className="flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loophover">
          <Plus className="h-4 w-4" />Add Activity
        </button>
      </div>
    </div>
  );

  if (error) return <div className="space-y-6">{header}<ErrorView message="Couldn't load activities. Please try again." onRetry={load} /></div>;

  const sections = data ? [
    { key: "overdue", title: "Overdue", icon: AlertTriangle, color: "text-red-500", items: data.overdue },
    { key: "due_today", title: "Due Today", icon: CalendarClock, color: "text-loop", items: data.due_today },
    { key: "upcoming", title: "Upcoming", icon: CalendarCheck, color: "text-emerald-600", items: data.upcoming },
  ].filter((s) => !overdueOnly || s.key === "overdue") : [];
  const activeEmpty = sections.every((s) => s.items.length === 0);

  return (
    <div className="space-y-6" data-testid="activities-page">
      {header}
      {showFilters && FilterPanel}
      <FilterChips chips={chips} onRemove={removeChip} onClearAll={clearAllChips} testid="activities-chips" />

      {loading ? <ActivitySkeleton /> : (
        <>
          <div className="flex gap-1 rounded-xl border border-sky/70 bg-white p-1">
            {[["active", "Active"], ["completed", `Completed (${data.completed.length})`], ["cancelled", `Cancelled (${data.cancelled.length})`]].map(([k, label]) => (
              <button key={k} data-testid={`activities-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "bg-loop text-white" : "text-ink/60 hover:bg-mist"}`}>{label}</button>
            ))}
          </div>

          {tab === "active" && (
            activeEmpty ? (
              <EmptyState icon={CalendarCheck} title="No activities due in this bucket" subtitle="You're all caught up — nothing matches your current filters." />
            ) : (
              <div className={`grid grid-cols-1 gap-5 ${overdueOnly ? "" : "lg:grid-cols-3"}`}>
                {sections.map((sec) => (
                  <div key={sec.key} data-testid={`activities-col-${sec.key}`}>
                    <div className="mb-3 flex items-center gap-2">
                      <sec.icon className={`h-4 w-4 ${sec.color}`} />
                      <h2 className="font-sora text-sm font-semibold text-navy">{sec.title}</h2>
                      <span className="rounded-full bg-mist px-2 py-0.5 text-xs font-bold text-ink/60">{sec.items.length}</span>
                    </div>
                    <div className="space-y-3">
                      {sec.items.length === 0 ? <p className="rounded-xl border border-dashed border-sky py-6 text-center text-xs text-ink/40">Nothing here</p> :
                        sec.items.map((a) => <ActivityCard key={a.id} a={a} busyId={busyId} onAction={action} onOpenLead={setSelected} onEdit={(x) => setModal(x)} onRepeat={setRepeatModal} />)}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "completed" && (
            data.completed.length === 0 ? <EmptyState icon={CheckCircle2} title="No completed activities yet" /> : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {data.completed.map((a) => <ActivityCard key={a.id} a={a} completed onOpenLead={setSelected} onAction={action} onEdit={() => {}} onRepeat={setRepeatModal} />)}
              </div>
            )
          )}

          {tab === "cancelled" && (
            data.cancelled.length === 0 ? <EmptyState icon={XCircle} title="No cancelled activities" /> : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {data.cancelled.map((a) => <ActivityCard key={a.id} a={a} completed onOpenLead={setSelected} onAction={action} onEdit={() => {}} />)}
              </div>
            )
          )}
        </>
      )}

      <LeadDrawer leadId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onUpdated={load} users={users} />
      <ActivityModal open={!!modal} onOpenChange={(v) => !v && setModal(null)} activity={modal !== "new" ? modal : null} users={users} onSaved={load} />
      <ActivityModal open={!!repeatModal} onOpenChange={(v) => !v && setRepeatModal(null)} repeat={repeatModal} leadId={repeatModal?.lead_id} leadName={repeatModal?.lead_name} users={users} onSaved={load} />
      <ConfirmDialog
        open={!!cancelId}
        onOpenChange={(v) => !v && setCancelId(null)}
        onConfirm={doCancel}
        title="Cancel this activity?"
        description="The activity will be marked as cancelled. No changes are made to the lead."
        confirmLabel="Cancel activity"
        cancelLabel="Keep it"
        testid="cancel-activity-dialog"
      />
    </div>
  );
}
